import { useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import YupPassword from 'yup-password'
YupPassword(yup)

import {
  REGISTER_FINANCES_USER_KEY,
  registerFinancesUser,
} from '@/actions/authActions'
import { getFetchError } from '@/reducers/fetch'
import { useSignupLastPageNavigate } from '@/services/onboardingService'
import { getFreeTrialStatus } from '@/selectors/user.selectors'

import {
  useNavigateWithPersistParams,
  useReselector,
  useScrollRef,
} from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { STATES } from '@/constants/locationConstants'
import { TosVersion } from '@/constants/businessConstants'

import { useAnalyticsSetUserId, useAnalyticsView } from '@/features/amplitude'

import {
  getFieldName,
  makeReqStringSchema,
  makeReqPhoneNumberSchema,
  makePasswordSchema,
  makeReqEmailSchema,
} from '@/base'
import { TextColors, TypographyStyles } from '@/styles'
import { SIGNUP_PATHS, isFreeTrialEnabled } from '../helpers'
import { Button, Text, Alert, Link, Form, Separator } from '@/ui'
import { SignupHeader } from '../SignupHeader'
import { FreeTrialSignupBanner } from '../FreeTrialSignupBanner'
import { WhatYouGetCard } from './WhatYouGetCard'
import { ReferralWelcomeMessage } from './WelcomeMessage'

// TODO: Migrate to V2 skipcq: JS-0099
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.slice'
import { useReferralFetch } from '@/featuresV1/Referrals/referrals.helpers'

const validationSchema = yup.object({
  firstName: makeReqStringSchema({ field: 'first name' }),
  lastName: makeReqStringSchema({ field: 'last name' }),
  email: makeReqEmailSchema({ field: 'email' }),
  phoneNumber: makeReqPhoneNumberSchema(),
  state: makeReqStringSchema({ field: 'practicing state' }),
  password: makePasswordSchema(),
  confirmPassword: makeReqStringSchema({ field: 'confirm password' }).test(
    'Password Test',
    'Passwords do not match',
    (confirmPassword, context) => {
      const password = context.parent['password']
      return password === confirmPassword
    }
  ),
  smsContactPhoneNumber: yup
    .string()
    .when('smsConsent', ([smsConsent], schema) =>
      smsConsent === true ? makeReqPhoneNumberSchema() : schema.nullable()
    ),
})

const TermsOfUseText = ({ className }: { className?: string }) => (
  <Text className={className} color={TextColors.MUTED}>
    By selecting <b>Continue</b>, you&apos;re creating a Heard account, and you
    agree to our{' '}
    <Link href="https://www.joinheard.com/terms-of-service" newPage>
      Terms of Use
    </Link>{' '}
    and{' '}
    <Link href="https://www.joinheard.com/privacy" newPage>
      Privacy Policy.
    </Link>
  </Text>
)

export const AboutYou = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigateWithPersistParams()
  const pageView = useAnalyticsView()
  const setAnalyticsUserId = useAnalyticsSetUserId()
  const { scrollRef, scrollToRef } = useScrollRef()
  const { recipientCode } = useParams()
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const signupError = useReselector(getFetchError, REGISTER_FINANCES_USER_KEY)
  const [searchParams] = useSearchParams()
  const basicPlanSearchParamValue = searchParams.get('subscription')
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)
  const isReferral = searchParams.get('trial') === 'referral'
  const consult = new URLSearchParams(location.search).get('consult')

  const useBasicTypePlan =
    basicPlanSearchParamValue === 'new' || basicPlanSearchParamValue === 'basic'

  const freeTrialEnabled = isFreeTrialEnabled(
    searchParams.get('trial'),
    userFreeTrialStatus,
    useBasicTypePlan
  )
  const { recipient, loading: recipientLoading } =
    useReferralFetch(recipientCode)

  useSignupLastPageNavigate()

  useEffect(() => {
    pageView('sign up about you')
  }, [pageView])

  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  const formik = useFormik({
    initialValues: {
      firstName: recipient?.firstName || '',
      lastName: recipient?.lastName || '',
      email: recipient?.email || '',
      phoneNumber: '',
      state: '',
      password: '',
      confirmPassword: '',
      smsConsent: false,
      smsContactPhoneNumber: '',
      sameAsBusinessPhoneNumber: false,
      freeTrialPromoCode: null,
    },
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema,
    onSubmit: async ({
      firstName,
      lastName,
      email,
      password,
      phoneNumber,
      state,
      smsConsent,
      smsContactPhoneNumber,
    }) => {
      const res = await registerFinancesUser({
        firstName,
        lastName,
        email,
        password,
        phoneNumber,
        state,
        freeTrialPromoCode: searchParams.get('trial')
          ? searchParams.get('trial')
          : null,
        smsContactPhoneNumber: smsConsent ? smsContactPhoneNumber : undefined,
        hadConsult: consult === 'true',
        tosVersion: TosVersion.tos_01_2024,
        tosAcceptedOn: Date.now(),
      })(dispatch)

      if (res) {
        setAnalyticsUserId(res.user.uuid)
        navigate(SIGNUP_PATHS.aboutYourPractice)
      } else {
        scrollToRef()
      }
    },
  })

  const { values, submitForm, isSubmitting, setFieldValue } = formik

  return (
    <>
      <div className="container mx-auto pt-16">
        <div className="flex flex-col">
          <SignupHeader currentStep={0} />

          {signupError && (
            <div className="w-full mb-4">
              <span ref={scrollRef} />
              <Alert variant="error" description={signupError.message} />
            </div>
          )}

          {recipientCode && (
            <ReferralWelcomeMessage
              freeTrialEnabled={freeTrialEnabled}
              recipient={recipient}
              recipientLoading={recipientLoading}
            />
          )}

          {!recipientCode && (
            <>
              <div className="w-full mb-4">
                <Text>{freeTrialEnabled && <FreeTrialSignupBanner />}</Text>
              </div>

              <div className="w-full mb-4">
                <Text variant={TypographyStyles.H1}>
                  {isReferral ? 'Welcome' : 'Tell us about yourself'}
                </Text>
              </div>
            </>
          )}

          <section className="flex flex-col md:flex-row justify-between gap-8">
            <div className="w-full md:w-1/2">
              <FormikProvider value={formik}>
                <div className={`grid gap-4 ${isMobile ? 'px-4' : ''}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Form.Input
                      label="First Name"
                      name={getFieldName<typeof values>('firstName')}
                    />

                    <Form.Input
                      label="Last Name"
                      name={getFieldName<typeof values>('lastName')}
                    />
                  </div>

                  <Form.Input
                    className="w-full mb-4"
                    label="Business Email Address"
                    disabled={Boolean(recipient?.email)}
                    name={getFieldName<typeof values>('email')}
                  />

                  <Form.Input
                    label="Business Phone"
                    placeholder="555-555-5555"
                    componentType="phone"
                    name={getFieldName<typeof values>('phoneNumber')}
                  />

                  <Form.Checkbox
                    label="Get important tax and bookeeping reminders via SMS"
                    name={getFieldName<typeof values>('smsConsent')}
                  />

                  {values.smsConsent && (
                    <div>
                      <div className="ml-10 flex flex-col gap-4">
                        {!values.sameAsBusinessPhoneNumber && (
                          <Form.Input
                            label="What mobile phone do you want text reminders sent
                                  to?"
                            placeholder="555-555-5555"
                            componentType="phone"
                            name={getFieldName<typeof values>(
                              'smsContactPhoneNumber'
                            )}
                          />
                        )}

                        <Form.Checkbox
                          className="mb-2"
                          label="Same as business phone"
                          name={getFieldName<typeof values>(
                            'sameAsBusinessPhoneNumber'
                          )}
                          onCheckedChange={(checked) => {
                            setFieldValue('sameAsBusinessPhoneNumber', checked)
                            setFieldValue(
                              'smsContactPhoneNumber',
                              checked ? values.phoneNumber : ''
                            )
                          }}
                        />

                        <div className="mb-2">
                          <Text
                            variant={TypographyStyles.BODY_XS}
                            className="text-gray"
                          >
                            You agree to receive recurring text messages from
                            Heard about account updates and service reminders at
                            the mobile phone number provided. Messages frequency
                            varies. Message and date rates may apply. Reply STOP
                            to cancel. View our{' '}
                            <Link
                              variant="secondary"
                              newPage
                              href="https://www.joinheard.com/terms-of-service"
                            >
                              Terms of Service
                            </Link>{' '}
                            and{' '}
                            <Link
                              variant="secondary"
                              newPage
                              href="https://www.joinheard.com/privacy"
                            >
                              Privacy Policy
                            </Link>
                            .
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}

                  <Form.Autocomplete
                    label="Home State"
                    placeholder="Select State"
                    description="Your primary residence is in this state."
                    name={getFieldName<typeof values>('state')}
                    options={STATES.map((state) => state.text)}
                  />

                  <Form.Input
                    required
                    label="Password"
                    description="Your password needs to have more than 8 characters, and must include a number and a symbol."
                    name={getFieldName<typeof values>('password')}
                    type="password"
                  />

                  <Form.Input
                    required
                    label="Confirm Password"
                    name={getFieldName<typeof values>('confirmPassword')}
                    type="password"
                  />
                </div>
              </FormikProvider>
            </div>

            {!isMobile && <WhatYouGetCard className="w-5/12" />}
          </section>

          {isMobile && (
            <>
              <Button
                className="w-full mb-4"
                onClick={submitForm}
                disabled={isSubmitting}
                loading={isSubmitting}
                id="btn-signup-create-account"
              >
                Continue
              </Button>

              <Button
                className="w-full mb-4"
                variant="secondary"
                onClick={() => navigate('/')}
              >
                Back
              </Button>

              <TermsOfUseText className="w-full mb-8" />
            </>
          )}

          {!isMobile && (
            <>
              <Separator className="my-3" />

              <div className="flex justify-between items-center w-full">
                <Button variant="secondary" onClick={() => navigate('/')}>
                  Back
                </Button>

                <Button
                  onClick={submitForm}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  id="btn-signup-create-account"
                >
                  Continue
                </Button>
              </div>

              <TermsOfUseText className="w-2/3 mt-6 mb-8" />
            </>
          )}
        </div>
      </div>

      {isMobile && <WhatYouGetCard />}
    </>
  )
}
